<ng-template
  let-extraClass="extraClass"
  #topNav>
  @if (!isRightSideContent) {
    <tidy-top-nav
      [class]="extraClass"
      [canGoBack]="true"
      [title]="'Insurance Quote'" />
  }
</ng-template>

<ng-container *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<ng-template #content>
  <tidy-wrapper [class]="isRightSideContent ? 'right-side-scroll-enabled' : ''">
    @if (errorMessage) {
      <tidy-error-message
        [text]="errorMessage" />
    }

    @if (loaded && estimate) {

      <tidy-card class="sq-hero sq-animate">
        <div class="sq-hero__badge">
          <span class="sq-hero__badge-label">{{ 'Powered by' | translate }}</span>
          <img
            class="sq-hero__logo"
            src="assets/img/steadily-logo.svg"
            alt="Steadily" />
        </div>
        <div class="sq-hero__property">
          <tidy-text
            [header]="propertyName"
            [translate]="false" />
          @if (addressLine) {
            <tidy-text
              [helper]="addressLine"
              [translate]="false" />
          }
        </div>
        <div class="sq-hero__price">
          <span class="sq-hero__amount">${{ displayedMonthly() }}</span>
          <span class="sq-hero__per">/mo</span>
        </div>
        <tidy-text
          [helper]="annualPremiumLabel"
          [translate]="false" />
        @if (annualSavings > 0) {
          <div class="sq-hero__savings">
            <tidy-image
              [src]="'assets/svg/trending-down-outline.svg'"
              class="body-size" />
            <span>{{ savingsLabel }}</span>
          </div>
        }
      </tidy-card>

      @if (comparisonBars.length > 0) {
        <tidy-card class="sq-compare sq-animate sq-animate--2">
          <tidy-row class="extra-bottom-padding">
            <tidy-text
              [header]="'Annual Premium Comparison'" />
          </tidy-row>
          @for (bar of comparisonBars; track bar.label) {
            <div class="sq-compare__row">
              <div class="sq-compare__meta">
                <tidy-text
                  [body]="bar.label"
                  [translate]="false" />
                <tidy-text
                  [body]="bar.amountLabel"
                  [translate]="false"
                  class="bold" />
              </div>
              <div class="sq-compare__track">
                <div
                  class="sq-compare__fill"
                  [ngClass]="'tone-' + bar.tone"
                  [style.width.%]="bar.widthPct">
                </div>
              </div>
            </div>
          }
        </tidy-card>
      }

      @if (plans.length > 0) {
        <div class="sq-plans">
          @for (plan of plans; track plan.product; let i = $index) {
            <tidy-card
              class="sq-plan sq-animate"
              [ngClass]="'sq-animate--' + (i + 3)"
              [class.is-recommended]="plan.isRecommended"
              role="button"
              tabindex="0"
              (click)="selectPlan()"
              (keydown.enter)="selectPlan()">
              @if (plan.isRecommended) {
                <div class="sq-plan__ribbon">{{ 'Recommended' | translate }}</div>
              }
              <div class="sq-plan__head">
                <div>
                  <tidy-text
                    [header]="plan.name"
                    [translate]="false" />
                  <br>
                  <tidy-text
                    [helper]="plan.description"
                    [translate]="false" />
                </div>
                <div class="sq-plan__price">
                  <tidy-text
                    [header]="plan.monthly"
                    [translate]="false" />
                  <tidy-text
                    [helper]="plan.annual"
                    [translate]="false" />
                </div>
              </div>
              <tidy-divider />
              @if (plan.coverageLines.length > 0) {
                <div class="sq-plan__coverages">
                  @for (line of plan.coverageLines; track line.label) {
                    <div class="sq-plan__coverage">
                      <span class="sq-plan__coverage-label">{{ line.label | translate }}</span>
                      <span class="sq-plan__coverage-value">{{ line.value }}</span>
                    </div>
                  }
                </div>
              }
              <div class="sq-plan__cta">{{ 'Continue with this plan' | translate }}</div>
            </tidy-card>
          }
        </div>
      }

      <tidy-card class="sq-embed-card sq-animate sq-animate--5">
        <tidy-row class="extra-bottom-padding">
          <tidy-text
            [header]="'Start Your Application'" />
        </tidy-row>
        <tidy-row class="extra-bottom-padding">
          <tidy-text
            [body]="'Your property details are prefilled. Finish in about 5 minutes — no phone call needed.'" />
        </tidy-row>
        <!-- The inline preview only works on the web. Native WebViews commonly block the
             cross-origin frame, so on iOS/Android we skip it and open the funnel in the
             system browser (Capacitor Browser) via the button below instead. -->
        @if (!isNativeApp) {
          <div class="sq-embed">
            <div class="sq-embed__chrome">
              <span class="sq-embed__dot"></span>
              <span class="sq-embed__dot"></span>
              <span class="sq-embed__dot"></span>
              <span class="sq-embed__url">steadily.com</span>
              <button
                type="button"
                class="sq-embed__open"
                (click)="openApplication()">
                <img
                  class="sq-embed__open-icon"
                  src="assets/svg/browsers-outline.svg"
                  alt="" />
                {{ 'Open Full Application' | translate }}
              </button>
            </div>
            <div class="sq-embed__frame-wrap">
              @if (!iframeLoaded()) {
                <div class="sq-embed__loading">
                  <ion-spinner name="crescent" />
                  @if (showEmbedHint()) {
                    <tidy-text
                      [helper]="'Preview not loading? Use the button below to open the full application.'" />
                  }
                </div>
              }
              @if (embedUrl) {
                <iframe
                  [src]="embedUrl"
                  (load)="onIframeLoad()"
                  class="sq-embed__frame"
                  [class.is-loaded]="iframeLoaded()"
                  frameborder="0"
                  title="Steadily Application">
                </iframe>
              }
            </div>
          </div>
        }
        @if (isNativeApp) {
          <tidy-row class="extra-top-padding">
            <tidy-button
              [text]="'Open Full Application'"
              [action]="boundOpenApplication"
              class="primary" />
          </tidy-row>
        }
        <tidy-row [align]="'center'" class="extra-top-padding">
          <tidy-text
            [helper]="'Estimates powered by Steadily. Final price is set after underwriting.'" />
        </tidy-row>
      </tidy-card>
    }

    @if (loaded && !estimate && !errorMessage) {
      <tidy-alert>
        <tidy-row>
          <tidy-text
            [body]="'This quote is no longer available. Go back and request a new estimate.'" />
        </tidy-row>
      </tidy-alert>
    }
  </tidy-wrapper>
</ng-template>

@if (!isRightSideContent) {
  <app-scroll-wrapper [templateContent]="content" />
}

@if (isRightSideContent) {
  <!-- No <ion-content> here: it collapses to zero height inside the desktop dialog's
       plain scroll container and clips its content. The dialog already scrolls. -->
  <ng-container *ngTemplateOutlet="content" />
}
